import React, { useMemo, useState, useEffect } from 'react';
import api from '../utils/api';
import { toast } from 'react-toastify';
import './AdminAPIKeyManager.css';

const AdminAPIKeyManager = ({ chainUsers = [] }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterHasApiKey, setFilterHasApiKey] = useState('all');
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ spotifyApiKey: '', spotifyRefreshToken: '' });
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkUpdates, setBulkUpdates] = useState([]);

  const getPageItems = (current, total) => {
    if (total <= 1) return [1];
    const items = [];
    const windowSize = 2;
    const start = Math.max(2, current - windowSize);
    const end = Math.min(total - 1, current + windowSize);

    items.push(1);
    if (start > 2) items.push('ellipsis-left');
    for (let i = start; i <= end; i += 1) {
      items.push(i);
    }
    if (end < total - 1) items.push('ellipsis-right');
    items.push(total);
    return items;
  };

  const chainUserMap = useMemo(() => {
    const m = new Map();
    (chainUsers || []).forEach((u) => {
      if (!u?.walletAddress) return;
      m.set(u.walletAddress.toLowerCase(), {
        totalNFTs: u.totalNFTs || 0,
        tokenIds: Array.isArray(u.nftsOwned) ? u.nftsOwned : []
      });
    });
    return m;
  }, [chainUsers]);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      // Load all DB users so we can join with on-chain owners locally.
      // The table itself will show only NFT owners (on-chain) + admin wallets.
      const params = new URLSearchParams({
        page: '1',
        limit: '2000'
      });
      const response = await api.get(`/admin/users?${params}`);
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const displayedUsers = useMemo(() => {
    const chainWalletsLower = new Set(Array.from(chainUserMap.keys()));

    // 1) Start with on-chain owners (even if they don't exist in DB yet)
    const rows = Array.from(chainWalletsLower).map((walletLower) => {
      const dbUser = users.find(u => (u.walletAddress || '').toLowerCase() === walletLower);
      if (dbUser) return dbUser;
      return {
        _id: walletLower,
        walletAddress: walletLower,
        nftsOwned: [],
        totalRevenue: 0,
        isAdmin: false,
        hasSpotifyApiKey: false,
        hasSpotifyRefreshToken: false,
        createdAt: null,
        lastLogin: null,
        __placeholder: true
      };
    });

    // 2) Add any admin users from DB even if they don't hold NFTs
    users
      .filter(u => u?.isAdmin)
      .forEach((adminUser) => {
        const lower = (adminUser.walletAddress || '').toLowerCase();
        if (!lower) return;
        if (!rows.some(r => (r.walletAddress || '').toLowerCase() === lower)) {
          rows.push(adminUser);
        }
      });

    // Local filters
    let filtered = rows;
    if (searchTerm?.trim()) {
      const q = searchTerm.trim().toLowerCase();
      filtered = filtered.filter(u => (u.walletAddress || '').toLowerCase().includes(q));
    }
    if (filterHasApiKey === 'true') {
      filtered = filtered.filter(u => !!u.hasSpotifyApiKey);
    } else if (filterHasApiKey === 'false') {
      filtered = filtered.filter(u => !u.hasSpotifyApiKey);
    }

    // Sort: owners with more NFTs first, then admins
    filtered.sort((a, b) => {
      const aChain = chainUserMap.get((a.walletAddress || '').toLowerCase());
      const bChain = chainUserMap.get((b.walletAddress || '').toLowerCase());
      const aN = aChain?.totalNFTs ?? 0;
      const bN = bChain?.totalNFTs ?? 0;
      if (bN !== aN) return bN - aN;
      if (!!b.isAdmin !== !!a.isAdmin) return b.isAdmin ? 1 : -1;
      return (a.walletAddress || '').localeCompare(b.walletAddress || '');
    });

    // Pagination derived from filtered list
    const pages = Math.max(1, Math.ceil(filtered.length / limit));
    if (page > pages) setPage(1);
    setTotalPages(pages);

    const start = (page - 1) * limit;
    return filtered.slice(start, start + limit);
  }, [users, chainUserMap, searchTerm, filterHasApiKey, page, limit]);

  const handleEdit = (user) => {
    setEditingUser(user.walletAddress);
    setEditForm({
      spotifyApiKey: '',
      spotifyRefreshToken: ''
    });
  };

  const handleCancelEdit = () => {
    setEditingUser(null);
    setEditForm({ spotifyApiKey: '', spotifyRefreshToken: '' });
  };

  const handleSave = async (walletAddress) => {
    try {
      await api.put(`/admin/users/${walletAddress}/api-keys`, {
        spotifyApiKey: editForm.spotifyApiKey || null,
        spotifyRefreshToken: editForm.spotifyRefreshToken || null
      });

      toast.success('API keys updated successfully');
      setEditingUser(null);
      setEditForm({ spotifyApiKey: '', spotifyRefreshToken: '' });
      fetchUsers();
    } catch (error) {
      console.error('Error updating API keys:', error);
      toast.error(error.response?.data?.message || 'Failed to update API keys');
    }
  };

  const handleClearApiKeys = async (walletAddress) => {
    if (!window.confirm('Are you sure you want to clear API keys for this user?')) {
      return;
    }

    try {
      await api.put(`/admin/users/${walletAddress}/api-keys`, {
        spotifyApiKey: '',
        spotifyRefreshToken: ''
      });

      toast.success('API keys cleared successfully');
      fetchUsers();
    } catch (error) {
      console.error('Error clearing API keys:', error);
      toast.error('Failed to clear API keys');
    }
  };

  const handleBulkUpdate = async () => {
    if (bulkUpdates.length === 0) {
      toast.warning('No updates to process');
      return;
    }

    if (!window.confirm(`Are you sure you want to update ${bulkUpdates.length} users?`)) {
      return;
    }

    try {
      const response = await api.post('/admin/users/bulk-update-api-keys', {
        updates: bulkUpdates
      });

      toast.success(response.data.message);
      setBulkMode(false);
      setBulkUpdates([]);
      fetchUsers();
    } catch (error) {
      console.error('Error bulk updating:', error);
      toast.error(error.response?.data?.message || 'Bulk update failed');
    }
  };

  const addBulkUpdate = (walletAddress, spotifyApiKey, spotifyRefreshToken) => {
    const existingIndex = bulkUpdates.findIndex(u => u.walletAddress === walletAddress);
    const update = {
      walletAddress,
      ...(spotifyApiKey && { spotifyApiKey }),
      ...(spotifyRefreshToken && { spotifyRefreshToken })
    };

    if (existingIndex >= 0) {
      const newUpdates = [...bulkUpdates];
      newUpdates[existingIndex] = { ...newUpdates[existingIndex], ...update };
      setBulkUpdates(newUpdates);
    } else {
      setBulkUpdates([...bulkUpdates, update]);
    }
  };

  return (
    <div className="api-key-manager">
      <div className="api-key-manager-header">
        <h2>API Key Management</h2>
        <div className="header-actions">
          <button
            className="btn btn-secondary"
            onClick={() => {
              setBulkMode(!bulkMode);
              setBulkUpdates([]);
            }}
          >
            {bulkMode ? 'Exit Bulk Mode' : 'Bulk Update Mode'}
          </button>
        </div>
      </div>

      <div className="filters">
        <input
          type="text"
          placeholder="Search by wallet address..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(1);
          }}
          className="search-input"
        />
        <select
          value={filterHasApiKey}
          onChange={(e) => {
            setFilterHasApiKey(e.target.value);
            setPage(1);
          }}
          className="filter-select"
        >
          <option value="all">All Users</option>
          <option value="true">Has API Keys</option>
          <option value="false">No API Keys</option>
        </select>
      </div>

      {bulkMode && bulkUpdates.length > 0 && (
        <div className="bulk-update-banner">
          <p>{bulkUpdates.length} user(s) queued for bulk update</p>
          <button className="btn btn-primary" onClick={handleBulkUpdate}>
            Apply Bulk Update
          </button>
        </div>
      )}

      {loading ? (
        <div className="loading">Loading users...</div>
      ) : (
        <>
          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Wallet Address</th>
                  <th>NFTs Owned</th>
                  <th>API Key Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="empty-state">
                      No users found
                    </td>
                  </tr>
                ) : (
                  displayedUsers.map((user) => (
                    <tr key={user._id}>
                      <td className="wallet-cell">
                        <code>{user.walletAddress}</code>
                      </td>
                      <td>
                        {(() => {
                          const chain = chainUserMap.get((user.walletAddress || '').toLowerCase());
                          const chainCount = chain?.totalNFTs ?? 0;
                          const chainTokenIds = chain?.tokenIds ?? [];
                          const dbCount = user.nftsOwned?.length || 0;
                          const countToShow = chainCount || dbCount;
                          const tokenIdsToShow = chainTokenIds.length ? chainTokenIds : (user.nftsOwned || []).map(n => n.tokenId).filter(Boolean);

                          return (
                            <>
                              <strong style={{ color: 'var(--gold-primary)' }}>
                                {countToShow}
                              </strong>{' '}
                              NFT{countToShow !== 1 ? 's' : ''}
                              <span style={{ marginLeft: 8, fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>
                                {chainCount ? '(on-chain)' : '(db)'}
                              </span>

                              {tokenIdsToShow.length > 0 && (
                                <div className="nft-tooltip">
                                  {tokenIdsToShow.map((tokenId, idx) => (
                                    <span key={`${tokenId}-${idx}`}>#{tokenId} </span>
                                  ))}
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </td>
                      <td>
                        {editingUser === user.walletAddress ? (
                          <div className="edit-form">
                            <input
                              type="text"
                              placeholder="Spotify API Key"
                              value={editForm.spotifyApiKey}
                              onChange={(e) => setEditForm({ ...editForm, spotifyApiKey: e.target.value })}
                              className="form-input"
                            />
                            <input
                              type="text"
                              placeholder="Spotify Refresh Token"
                              value={editForm.spotifyRefreshToken}
                              onChange={(e) => setEditForm({ ...editForm, spotifyRefreshToken: e.target.value })}
                              className="form-input"
                            />
                          </div>
                        ) : (
                          <div className="api-key-status">
                            <span className={`status-badge ${user.hasSpotifyApiKey ? 'has-key' : 'no-key'}`}>
                              {user.hasSpotifyApiKey ? '✓ API Key' : '✗ No API Key'}
                            </span>
                            <span className={`status-badge ${user.hasSpotifyRefreshToken ? 'has-key' : 'no-key'}`}>
                              {user.hasSpotifyRefreshToken ? '✓ Refresh Token' : '✗ No Token'}
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="actions-cell">
                        {editingUser === user.walletAddress ? (
                          <div className="edit-actions">
                            <button
                              className="btn btn-small btn-primary"
                              onClick={() => handleSave(user.walletAddress)}
                            >
                              Save
                            </button>
                            <button
                              className="btn btn-small btn-secondary"
                              onClick={handleCancelEdit}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div className="action-buttons">
                            <button
                              className="btn btn-small btn-primary"
                              onClick={() => handleEdit(user)}
                            >
                              Edit
                            </button>
                            {user.hasSpotifyApiKey && (
                              <button
                                className="btn btn-small btn-danger"
                                onClick={() => handleClearApiKeys(user.walletAddress)}
                              >
                                Clear
                              </button>
                            )}
                            {bulkMode && (
                              <button
                                className="btn btn-small btn-secondary"
                                onClick={() => addBulkUpdate(user.walletAddress, '', '')}
                              >
                                Add to Bulk
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="pagination">
            <button
              className="btn btn-secondary"
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
            >
              Previous
            </button>
            <div className="pagination-numbers">
              {getPageItems(page, totalPages).map((item) => {
                if (typeof item !== 'number') {
                  return (
                    <span key={item} className="pagination-ellipsis">…</span>
                  );
                }
                return (
                  <button
                    key={item}
                    className={`pagination-page ${item === page ? 'active' : ''}`}
                    onClick={() => setPage(item)}
                    disabled={item === page}
                  >
                    {item}
                  </button>
                );
              })}
            </div>
            <button
              className="btn btn-secondary"
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page >= totalPages}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminAPIKeyManager;
